<template>
  <router-link to="/status" class="status-indicator" :class="`status-${status}`" :title="statusTitle">
    <p class="stat-text">System Status</p>
    <span class="status-dot"></span>
  </router-link>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import apiClient from '@/services/api'

type SystemStatus = 'operational' | 'degraded' | 'outage' | 'unknown'

const status = ref<SystemStatus>('unknown')
const lastChecked = ref<Date | null>(null)
let intervalId: number | null = null

const statusText = computed(() => {
  switch (status.value) {
    case 'operational': return 'All Systems Operational'
    case 'degraded': return 'Some Services Degraded'
    case 'outage': return 'Service Outage'
    default: return 'Status Unknown'
  }
})

const statusTitle = computed(() => {
  const lastCheckedText = lastChecked.value
    ? `Last checked: ${lastChecked.value.toLocaleTimeString()}`
    : 'Checking status...'
  return `${statusText.value}\n${lastCheckedText}\nClick for details`
})

async function checkStatus() {
  try {
    const response = await apiClient.get('/api/status')
    const data = response.data

    // Determine overall status based on core services
    const { api, database, firebase, supabase } = data.coreServices
    const allOnline = api.status === 'online' &&
                      database.status === 'online' &&
                      firebase.status === 'online' &&
                      supabase.status === 'online'

    if (allOnline) {
      status.value = 'operational'
    } else if (api.status === 'offline' || database.status === 'offline') {
      status.value = 'outage'
    } else {
      status.value = 'degraded'
    }

    lastChecked.value = new Date()
  } catch (error) {
    // If we can't reach the API, mark as outage
    status.value = 'outage'
    lastChecked.value = new Date()
  }
}

onMounted(() => {
  // Check status immediately
  checkStatus()

  // Check status every 60 seconds
  intervalId = window.setInterval(checkStatus, 60000)
})

onUnmounted(() => {
  if (intervalId) {
    clearInterval(intervalId)
  }
})
</script>

<style scoped>
.status-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.8rem;
  border-radius: 25px;
  text-decoration: none;
  transition: all 0.2s;
  border: 1px solid #262626;
}

.status-indicator:hover {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  animation: pulse 2s ease-in-out infinite;
}


@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.stat-text {
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  white-space: nowrap;
}

/* Status colors */
.status-operational .status-dot {
  background: #10b981;
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.5);
}

.status-operational .status-text {
  color: #10b981;
}

.status-degraded .status-dot {
  background: #f59e0b;
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.5);
}

.status-degraded .status-text {
  color: #f59e0b;
}

.status-outage .status-dot {
  background: #ef4444;
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.5);
}

.status-outage .status-text {
  color: #ef4444;
}

.status-unknown .status-dot {
  background: #6b7280;
  box-shadow: 0 0 8px rgba(107, 114, 128, 0.5);
}

.status-unknown .status-text {
  color: #9ca3af;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .status-text {
    display: none;
  }

  .status-indicator {
    padding: 0.5rem;
  }
}
</style>
